/**
 * Online Booking vs Business Booking Chart Component
 *
 * Donut chart showing the breakdown between online and business bookings.
 * Uses Recharts PieChart with custom center label and legend.
 */

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import styles from './BookingTypeChart.module.scss';

// Chart colors matching the design
const CHART_COLORS = {
    businessBooked: '#5E8677', // Dark green
    onlineBooking: '#BDD2CA' // Light green
} as const;

// Data structure for the chart
interface BookingData {
    name: string;
    value: number;
    percentage: number;
    color: string;
}

// Data matching the design exactly
const bookingData: BookingData[] = [
    {
        name: 'Business-booked',
        value: 510,
        percentage: 80,
        color: CHART_COLORS.businessBooked
    },
    {
        name: 'Online booking',
        value: 200,
        percentage: 20,
        color: CHART_COLORS.onlineBooking
    }
];

// Calculate total
const totalBookings = bookingData.reduce((sum, item) => sum + item.value, 0);

// Custom center label component
const CenterLabel: React.FC = () => {
    return (
        <div className={styles.centerLabel}>
            <div className={styles.totalValue}>{totalBookings}</div>
            <div className={styles.totalPercentage}>100%</div>
        </div>
    );
};

// Custom legend component
const CustomLegend: React.FC = () => {
    return (
        <div className={styles.legend}>
            {bookingData.map((item, index) => (
                <div
                    key={index}
                    className={styles.legendItem}
                >
                    <div
                        className={styles.legendDot}
                        style={{ backgroundColor: item.color }}
                    />
                    <span className={styles.legendLabel}>{item.name}</span>
                    <span className={styles.legendValue}>
                        {item.value} ({item.percentage}%)
                    </span>
                </div>
            ))}
        </div>
    );
};

export const BookingTypeChart: React.FC = () => {
    return (
        <div className={styles.chartContainer}>
            {/* Chart Title */}
            <h3 className={styles.chartTitle}>Online Booking vs Business booking</h3>

            {/* Donut Chart */}
            <div className={styles.chartWrapper}>
                <ResponsiveContainer
                    width="100%"
                    height={260}
                >
                    <PieChart>
                        <Pie
                            data={bookingData}
                            cx="50%"
                            cy="50%"
                            innerRadius={70}
                            outerRadius={100}
                            startAngle={90}
                            endAngle={450}
                            dataKey="value"
                            stroke="none"
                        >
                            {bookingData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.color}
                                />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>

                {/* Center Label */}
                <CenterLabel />
            </div>

            {/* Custom Legend */}
            <CustomLegend />
        </div>
    );
};
